/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ColumnInstance } from 'react-table';
import { Text, BrandColors } from '@blend/enterprise-components';
import { DateTime } from 'luxon';
import { NotaryAPI } from '../../../api/notary/types';
import { SignerCell } from '../Rooms/SignerCell';
import { StatusCell } from '../Rooms/StatusCell';
import { IconCell } from '../Rooms/IconCell';
import { AssignmentActions } from './AssignmentActions';
import { FilterParams } from '@shared/types/pipeline';
import { formatIsoDate, titleCase } from '@utils/formatting';
import { toUpper } from '@utils/lodash';
import { getAppointmentTimeValues, getNotaryTaskStatus } from '@utils/pipelineUtils';

const styles = StyleSheet.create({
  tableHeaderContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  tag: { margin: 0 },
});

const TableHeader: React.SFC<{ column: ColumnInstance<NotaryAPI.RoomListItem> }> = ({ column }) => {
  return (
    <div className={css(styles.tableHeaderContainer)}>
      <Text color={BrandColors.NIGHT} fontSize="sm" weight="semibold">
        {toUpper(column.id)}
      </Text>
    </div>
  );
};

const TableCell: React.FC<{ value: string }> = ({ value }) => {
  return <Text>{value}</Text>;
};

const ActionCell: React.FC<{
  room: NotaryAPI.RoomListItem;
  fetchRooms: () => Promise<void>;
}> = ({ room, fetchRooms }) => {
  return <AssignmentActions room={room} fetchRooms={fetchRooms} />;
};

interface BaseColumnConfig {
  Cell: React.FC<{ value: string }>;
  Header: React.FC<{ column: ColumnInstance<any> }>;
}

const baseColumnConfig: BaseColumnConfig = {
  Cell: TableCell,
  Header: TableHeader,
};

export const getColumnConfigurations = (
  fetchRooms: (filters: FilterParams) => Promise<void>,
  filters: FilterParams,
) => [
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.status,
    Cell: StatusCell,
    id: ' ',
    width: '3%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => formatIsoDate(room.scheduleDate),
    id: 'Closing Date',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.state,
    id: 'State',
    width: '3%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => titleCase(room.externalOrganization),
    id: 'Organization',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room.signers,
    Cell: SignerCell,
    id: 'Signer(s)',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => {
      return getAppointmentTimeValues(room.preferredTimeSlots, room.scheduleTime);
    },
    Cell: IconCell,
    id: `Appointment Time (${DateTime.fromISO(new Date().toISOString()).toFormat(`ZZZZ`)})`,
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => getNotaryTaskStatus('Lender Documents', room.notaryTasks),
    Cell: IconCell,
    id: 'Lender Documents',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => getNotaryTaskStatus('Title Documents', room.notaryTasks),
    Cell: IconCell,
    id: 'Title Documents',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (room: NotaryAPI.RoomListItem) => room,
    Cell: ({ value }: { value: NotaryAPI.RoomListItem }) => (
      <ActionCell room={value} fetchRooms={() => fetchRooms(filters)} />
    ),
    id: 'Action',
    width: '5%',
  },
];
